/**
 * LazyVideo — defer video downloads until near viewport.
 *
 * Finds all `<video data-lazy>` elements inside a root and:
 * 1. On first intersection with a generous rootMargin, swaps `data-src` → `src`
 *    (or simply calls .load()) and starts playback.
 * 2. When a video leaves the viewport, calls .pause() to stop decoding /
 *    network buffering for looping videos.
 * 3. On tab blur (visibilitychange), pauses all tracked videos.
 *
 * Videos without `data-lazy` (e.g. the home reel) are left alone.
 */

export class LazyVideoManager {
  private io: IntersectionObserver | null = null;
  private tracked = new WeakSet<HTMLVideoElement>();
  private active = new Set<HTMLVideoElement>();
  private onVisibility: (() => void) | null = null;

  /**
   * Scan `root` for lazy videos and begin observing them.
   * Call on page init (after DOM is in place).
   */
  observe(root: Element | Document | null): void {
    if (!root) return;
    if (typeof IntersectionObserver === "undefined") {
      // Older browsers — fall back to eager load
      this.eagerFallback(root);
      return;
    }

    if (!this.io) {
      this.io = new IntersectionObserver(
        (entries) => this.handleIntersections(entries),
        {
          // Preload when within one viewport of the visible area
          rootMargin: "100% 0% 100% 0%",
          threshold: [0, 0.01],
        }
      );
    }

    if (!this.onVisibility) {
      this.onVisibility = () => {
        if (document.hidden) {
          for (const v of this.active) {
            try {
              v.pause();
            } catch {}
          }
        } else {
          for (const v of this.active) {
            // Only resume if still in viewport (IO will re-fire on scroll)
            v.play().catch(() => {});
          }
        }
      };
      document.addEventListener("visibilitychange", this.onVisibility);
    }

    const videos = root.querySelectorAll<HTMLVideoElement>("video[data-lazy]");
    for (const v of videos) {
      if (this.tracked.has(v)) continue;
      this.tracked.add(v);

      // Never auto-fetch before intersection — strip autoplay/preload declared in markup
      v.autoplay = false;
      if (!v.hasAttribute("preload")) {
        v.preload = "none";
      }
      this.io.observe(v);
    }
  }

  /**
   * Drop observer state for the previous page.
   * Keeps the global visibilitychange listener (lives with App singleton).
   */
  reset(): void {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    for (const v of this.active) {
      try {
        v.pause();
      } catch {}
    }
    this.active.clear();
    this.tracked = new WeakSet<HTMLVideoElement>();
  }

  /** Stop observing all videos, pause active ones. Call on full teardown. */
  disconnect(): void {
    this.reset();
    if (this.onVisibility) {
      document.removeEventListener("visibilitychange", this.onVisibility);
      this.onVisibility = null;
    }
  }

  private handleIntersections(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const v = entry.target as HTMLVideoElement;
      if (entry.isIntersecting) {
        this.activate(v);
      } else {
        this.deactivate(v);
      }
    }
  }

  private activate(v: HTMLVideoElement): void {
    // First activation: promote data-src → src
    const dataSrc = v.getAttribute("data-src");
    if (dataSrc && !v.src) {
      v.src = dataSrc;
      v.preload = "auto";
      // Keep data-src so WebGL scene can reference it if needed, but src is authoritative
    } else if (!v.src) {
      // No data-src, no src — nothing to load
      return;
    }

    this.active.add(v);

    const play = () => {
      // Muted + playsinline required for autoplay on iOS
      v.muted = true;
      v.playsInline = true;
      v.play().catch(() => {});
    };

    if (v.readyState >= 2) {
      play();
    } else {
      v.addEventListener("loadeddata", play, { once: true });
      // Ensure network fetch starts
      try {
        v.load();
      } catch {}
    }
  }

  private deactivate(v: HTMLVideoElement): void {
    if (!this.active.has(v)) return;
    this.active.delete(v);
    try {
      v.pause();
    } catch {}
  }

  /** Older browsers: just promote data-src → src and call it a day. */
  private eagerFallback(root: Element | Document): void {
    const videos = root.querySelectorAll<HTMLVideoElement>("video[data-lazy]");
    for (const v of videos) {
      const dataSrc = v.getAttribute("data-src");
      if (dataSrc && !v.src) v.src = dataSrc;
      v.play().catch(() => {});
    }
  }
}
